import * as THREE from 'three';
import * as BufferGeometryUtils from 'three/examples/jsm/utils/BufferGeometryUtils.js';

import { Product } from '@/config/products';



/**
 * Process Nylon Lanyard Geometry
 * Creates a merged "All Faces" print area from individual print areas
 */
export const processNylonLanyardGeometry = (
    sceneToProcess: THREE.Group, 
    product: Product | undefined, 
    printAreas: any[]
): THREE.Texture | null => {
    if (product?.id !== 'nylon-lanyard') return null;

    let nylonLanyardSharedTexture: THREE.Texture | null = null;
    const printAreaMeshes: THREE.Mesh[] = [];

    sceneToProcess.traverse((c: any) => {
        const name = c.name.toLowerCase();
        if (c.isMesh && (name.includes('printarea') || name.includes('print_area') || name.includes('imprint'))) {
            printAreaMeshes.push(c);
        }
    });

    if (printAreaMeshes.length > 0) {
        try {
            const geometriesToMerge: THREE.BufferGeometry[] = [];
            printAreaMeshes.forEach(mesh => {
                mesh.updateMatrix();
                const geom = mesh.geometry.clone();
                geom.applyMatrix4(mesh.matrix);
                geometriesToMerge.push(geom);
            });

            if (geometriesToMerge.length > 0) {
                if (BufferGeometryUtils && BufferGeometryUtils.mergeGeometries) {
                    const mergedGeom = BufferGeometryUtils.mergeGeometries(geometriesToMerge);
                    const allFacesMesh = new THREE.Mesh(mergedGeom, printAreaMeshes[0].material);
                    
                    allFacesMesh.name = "PrintArea_All_Faces";
                    allFacesMesh.userData.isPrintArea = true;
                    allFacesMesh.renderOrder = 2000;
                    
                    const wrapperMat = new THREE.MeshStandardMaterial({
                        color: 0xffffff,
                        transparent: true,
                        opacity: 0,
                        side: THREE.DoubleSide,
                        depthWrite: false,
                        roughness: 0.4,
                        metalness: 0.0
                    });
                    allFacesMesh.material = wrapperMat;

                    if (printAreaMeshes[0].parent) {
                        printAreaMeshes[0].parent.add(allFacesMesh);
                        
                        let normal = new THREE.Vector3(0, 0, 1);
                        if (mergedGeom && mergedGeom.getAttribute('normal')) {
                            const normalAttr = mergedGeom.getAttribute('normal');
                            normal.set(normalAttr.getX(0), normalAttr.getY(0), normalAttr.getZ(0));
                            normal.normalize();
                        }
                        
                        let borrowedDiffuseTexture: THREE.Texture | null = null;
                        sceneToProcess.traverse((sibling: any) => {
                            if (borrowedDiffuseTexture) return;
                            if (sibling.isMesh && sibling !== allFacesMesh) {
                                if (sibling.name.toLowerCase().includes('printarea') || 
                                    sibling.name.toLowerCase().includes('imprint')) {
                                return;
                                }
                                const materials = Array.isArray(sibling.material) ? sibling.material : [sibling.material];
                                for (const mat of materials) {
                                    if (mat instanceof THREE.MeshStandardMaterial && mat.map) {
                                    borrowedDiffuseTexture = mat.map;
                                    break;
                                    }
                                }
                            }
                        });
                        
                        if (!borrowedDiffuseTexture) {
                            const fabricCanvas = document.createElement('canvas');
                            fabricCanvas.width = 1024;
                            fabricCanvas.height = 1024;
                            const fabricCtx = fabricCanvas.getContext('2d')!;
                            fabricCtx.fillStyle = '#CCCCCC';
                            fabricCtx.fillRect(0, 0, 1024, 1024);
                            const imageData = fabricCtx.getImageData(0, 0, 1024, 1024);
                            const data = imageData.data;
                            for (let y = 0; y < 1024; y++) {
                                for (let x = 0; x < 1024; x++) {
                                    const i = (y * 1024 + x) * 4;
                                    const weaveX = Math.sin(x * 0.15) * 25;
                                    const weaveY = Math.sin(y * 0.15) * 25;
                                    const noise = (Math.random() - 0.5) * 40;
                                    const brightness = 204 + weaveX + weaveY + noise;
                                    data[i] = brightness;
                                    data[i + 1] = brightness;
                                    data[i + 2] = brightness;
                                }
                            }
                            fabricCtx.putImageData(imageData, 0, 0);
                            borrowedDiffuseTexture = new THREE.CanvasTexture(fabricCanvas);
                            borrowedDiffuseTexture.wrapS = THREE.RepeatWrapping;
                            borrowedDiffuseTexture.wrapT = THREE.RepeatWrapping;
                            borrowedDiffuseTexture.needsUpdate = true;
                        }
                        
                        nylonLanyardSharedTexture = borrowedDiffuseTexture;
                        
                        printAreas.push({
                            id: 'all-faces',
                            name: 'All Faces',
                            position: allFacesMesh.position.toArray(),
                            bounds: (() => {
                                const size = new THREE.Box3().setFromObject(allFacesMesh).getSize(new THREE.Vector3());
                                return { width: size.x, height: size.y };
                            })(),
                            normal: normal.toArray(),
                            meshRef: allFacesMesh,
                            parentMesh: sceneToProcess,
                            uvBounds: { minU: 0, maxU: 1, minV: 0, maxV: 1, needsFlipY: false },
                            borrowedDiffuseTexture: borrowedDiffuseTexture || undefined,
                            invertX: product?.materialConfig?.invertX,
                            invertY: false,
                            textureFlip: product?.materialConfig?.textureFlip,
                            textureFlipX: product?.materialConfig?.textureFlipX,
                        });
                    }
                }
            }
        } catch (e) {
            console.error("Failed to create All Faces for Nylon Lanyard:", e);
        }
    }
    return nylonLanyardSharedTexture;
};

/**
 * Process Square Pen Geometry
 * Merges the native PrintArea_Face meshes to create an "All Faces" wrapper
 */
export const processSquarePenGeometry = (
    sceneToProcess: THREE.Group, 
    product: Product | undefined, 
    printAreas: any[]
) => {
    if (product?.id !== 'square-pen') return;

    const bodiesToMerge: THREE.Mesh[] = [];
    sceneToProcess.traverse((c: any) => {
        const name = c.name.toLowerCase();
        if (c.isMesh && name.includes('printarea_face_')) {
            bodiesToMerge.push(c);
        }
    });


    if (bodiesToMerge.length > 0) {
        try {
            // Physical pen winding order based on local coordinates:
            // Face 1: ~47 deg (Front-Right)
            // Face 4: ~91 deg (Front-Left)
            // Face 3: ~133 deg (Back-Left/Opposite 1)
            // Face 2: ~0 or ~-45 deg (Back-Right/Opposite 4)
            // The user wants Face 1 to be the CENTER of the design.
            // When scaling up, it should spill onto Face 4 and Face 2, and finally meet at Face 3 (the back).
            // However, wrapping the UV mid-face (e.g. at U=0.5 on Face 3) causes the seam to appear in the middle of Face 3.
            // "on face 3 ends of imge are meeting in middle of face 3 , it should be on corner"
            // To fix this, the seam (U=0 and U=1) MUST be placed exactly on a geometric corner.
            // If Face 1 is center, and we read left-to-right (U=0 to U=1):
            // We want the sequence: [ Face 2 (Right) | Face 1 (Front/Center) | Face 4 (Left) | Face 3 (Back) ]
            // Wait, if Face 1 is front, and we rotate the pen:
            // Right edge of Face 1 perfectly meets Face 4 (based on previous adjacency)?
            // Adjacency graph: Face 1 is adjacent to Face 4. 
            // Our previous physical check: Face 1 (47 deg) next to Face 4 (91 deg) next to Face 3 (133 deg).
            // So: Face 2 -> Face 1 -> Face 4 -> Face 3.
            // Let's place the seam between Face 3 and Face 2 (the back-right corner).
            // If the UV map reads from U=0.0 to U=1.0 left-to-right on the 2D canvas:
            // Quarter 0 (0.00-0.25): Face 2
            // Quarter 1 (0.25-0.50): Face 1 (Front-Center) - wait, this places Face 1 off-center (at 0.375).
            // If Face 1 is at 0.375, an image placed at 0.5 will straddle Face 1 and Face 4!
            // If we absolutely MUST have the image center (U=0.5) land squarely in the middle of Face 1:
            // Face 1 MUST span [0.375, 0.625].
            // If Face 1 spans [0.375, 0.625], then:
            // The slot to its left [0.125, 0.375] is Face 2 (if we read right-to-left) or Face 4 (if left-to-right).
            // Let's say Face 4 is to its left (from the front perspective, Face 4 is left).
            // So Face 4 = [0.125, 0.375].
            // Face 2 is to its right (from the front perspective, Face 2 is right).
            // So Face 2 = [0.625, 0.875].
            // That leaves [0.875, 1.000] and [0.000, 0.125] for Face 3 (the back).
            // MATHEMATICALLY, if Face 1 is centered, and all faces are equal width, Face 3 MUST be split across the boundary of the texture.
            // There is no geometric way to have Face 1 exactly centered, all faces equal width, and NOT split the opposite face (Face 3).
            //
            // UNLESS we use a different UV repeating strategy.
            // BUT wait... the user says "img comes on face 1 , and when user scale it it will grow on face 4 and 2 and then on face 3".
            // If they want Face 3 to NOT be split in the middle, then the seam MUST be at a corner.
            // If the seam is at a corner, Face 1 CANNOT be exactly at U=0.5 in a 4-face layout.
            // Example:
            // Face 4: [0.00, 0.25]
            // Face 1: [0.25, 0.50]  <- Center of Face 1 is 0.375
            // Face 2: [0.50, 0.75]
            // Face 3: [0.75, 1.00]
            // In this case, Face 3 is unbroken. But a logo at U=0.5 lands identically on the border of Face 1 and Face 2.
            // 
            // How to fix? The standard solution is to shift the user's initial 2D logo placement natively in the component so it visually sits on Face 1 (e.g., positioned at X=0.375 or X=0.5 depending on mapping), while keeping the UV map clean (un-split).
            // Let's create a CLEAN, corners-only UV map:
            // [ Face 4 | Face 1 | Face 2 | Face 3 ]
            const windingStraddle: Record<string, number> = {
                'printarea_face_4': 0.00,   // U 0.00 - 0.25 (Left side)
                'printarea_face_1': 0.25,   // U 0.25 - 0.50 (Front)
                'printarea_face_2': 0.50,   // U 0.50 - 0.75 (Right side)
                'printarea_face_3': 0.75,   // U 0.75 - 1.00 (Back)
            };

            const geometriesToMerge: THREE.BufferGeometry[] = [];
            bodiesToMerge.forEach(body => {
                if (body.material) {
                    const materials = Array.isArray(body.material) ? body.material : [body.material];
                    materials.forEach(mat => {
                        if (mat instanceof THREE.MeshStandardMaterial && mat.map) {
                            mat.map.wrapS = THREE.ClampToEdgeWrapping;
                            mat.map.wrapT = THREE.ClampToEdgeWrapping;
                            mat.map.needsUpdate = true;
                        }
                    });
                }

                const transform = new THREE.Matrix4();
                let current: THREE.Object3D | null = body;
                const parents: THREE.Object3D[] = [];
                while(current && current !== sceneToProcess) {
                    parents.push(current);
                    current = current.parent;
                }
                transform.identity();
                for (let i = parents.length - 1; i >= 0; i--) {
                    parents[i].updateMatrix();
                    transform.multiply(parents[i].matrix);
                }

                const geom = body.geometry.clone();
                geom.applyMatrix4(transform);

                // --- NEW FIX: Physical Gasket Overlap ---
                // Expand the vertices horizontally relative to their local bounds
                // to force adjacent faces to intersect slightly and hide the background.
                const posAttr = geom.getAttribute('position');
                if (posAttr) {
                    let minX = Infinity, maxX = -Infinity;
                    for (let i = 0; i < posAttr.count; i++) {
                        minX = Math.min(minX, posAttr.getX(i));
                        maxX = Math.max(maxX, posAttr.getX(i));
                    }
                    const centerX = (minX + maxX) / 2;
                    const expansionFactor = 1.002;

                    for (let i = 0; i < posAttr.count; i++) {
                        const originalX = posAttr.getX(i);
                        // Push vertex outwards from local center by expansionFactor
                        const shiftedX = centerX + (originalX - centerX) * expansionFactor;
                        posAttr.setX(i, shiftedX);
                    }
                    posAttr.needsUpdate = true;
                    // Also update bounding info for the expanded geometry
                    geom.computeBoundingBox();
                    geom.computeBoundingSphere();
                }
                
                // Assign UVs based on physical winding position
                const faceKey = body.name.toLowerCase();
                const startU = windingStraddle[faceKey] ?? 0;
                const uvAttr = geom.getAttribute('uv');
                if (uvAttr) {
                    // Find this face's native UV range
                    let minU = Infinity, maxU = -Infinity;
                    let minV = Infinity, maxV = -Infinity;
                    for (let i = 0; i < uvAttr.count; i++) {
                        minU = Math.min(minU, uvAttr.getX(i));
                        maxU = Math.max(maxU, uvAttr.getX(i));
                        minV = Math.min(minV, uvAttr.getY(i));
                        maxV = Math.max(maxV, uvAttr.getY(i));
                    }
                    const rangeU = (maxU - minU) || 1;
                    const rangeV = (maxV - minV) || 1;

                    const OVERSHOOT = 0.005;

                    // Remap: normalize to 0-1 within face, then place into the correct straddle slot
                    for (let i = 0; i < uvAttr.count; i++) {
                        let localU = (uvAttr.getX(i) - minU) / rangeU; // 0-1 within face
                        let localV = (uvAttr.getY(i) - minV) / rangeV; // 0-1 within face

                        // Scale from center (0.5) to compress the UV mapping slightly inside [0..1]
                        localU = 0.5 + (localU - 0.5) * (1 - 2 * OVERSHOOT);
                        localV = 0.5 + (localV - 0.5) * (1 - 2 * OVERSHOOT);

                        let globalU = startU + (localU * 0.25);
                        
                        uvAttr.setX(i, globalU);       
                        uvAttr.setY(i, localV);                           
                    }
                    uvAttr.needsUpdate = true;
                }

                geometriesToMerge.push(geom);
            });

            if (geometriesToMerge.length > 0 && BufferGeometryUtils && BufferGeometryUtils.mergeGeometries) {
                const mergedGeom = BufferGeometryUtils.mergeGeometries(geometriesToMerge);

                const allFacesMesh = new THREE.Mesh(mergedGeom, bodiesToMerge[0].material);
                
                allFacesMesh.name = "PrintArea_All_Faces";
                allFacesMesh.userData.isPrintArea = true;
                allFacesMesh.userData.isAllFacesWrapper = true;
                
                allFacesMesh.renderOrder = 2500;
                
                const wrapperMat = new THREE.MeshStandardMaterial({
                    color: 0xffffff,
                    transparent: true,
                    opacity: 0, 
                    side: THREE.FrontSide,     // Only render outside
                    depthWrite: false,         // Don't obscure other transparent things
                    depthTest: true,           // DO depth test so backfaces don't show through
                    polygonOffset: true,       // Push forward to prevent z-fighting
                    polygonOffsetFactor: -4,
                    polygonOffsetUnits: -4,
                    roughness: 0.4,
                    metalness: 0.0
                });
                allFacesMesh.material = wrapperMat;

                if (bodiesToMerge[0].parent) {
                    bodiesToMerge[0].parent.add(allFacesMesh);
                    
                    printAreas.push({
                        id: 'all-faces',
                        name: 'All Faces',
                        position: allFacesMesh.position.toArray(),
                        bounds: (() => {
                            const size = new THREE.Box3().setFromObject(allFacesMesh).getSize(new THREE.Vector3());
                            const width = (size.x + size.y) * 2;
                            const height = size.z;
                            return { width, height };
                        })(),

                        normal: new THREE.Vector3(0, 0, 1),
                        meshRef: allFacesMesh,
                        parentMesh: sceneToProcess,
                        uvBounds: { minU: 0, maxU: 1, minV: 0, maxV: 1, needsFlipY: false },
                        borrowedDiffuseTexture: undefined,
                        invertX: product?.materialConfig?.invertX,
                        invertY: product?.materialConfig?.invertY ?? false,
                        textureFlip: product?.materialConfig?.textureFlip,
                        textureFlipX: product?.materialConfig?.textureFlipX,
                    });
                }
            }
        } catch (e) {
            console.error("Failed to merge Square Pen faces:", e);
        }
    }
};
